import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@/components/common/button/Button";
import Spinner from "@/components/common/spinner/Spinner";
import AlertMessage from "@/components/layout/message/AlertMessage";
import Card from "@/components/common/card/Card";
import HistoricalDataCard from "@/features/dashboard/components/cards/historical/HistoricalCard"; // Ajusta la ruta
import {
    Users,
    CheckCircle,
    XCircle,
    Building,
    ClipboardList,
    Clock,
    AlertTriangle,
    UserX,
    FileWarning,
    TrendingDown,
    Settings,
} from "lucide-react";
import styles from "./FacilitatorPanel.module.css";
import { useFacilitatorPanel } from "./useFacilitatorPanel";
import ManagementHeader from "../admin/managementHeader/ManagementHeader";
import AlertList from "./components/alertList/AlertList";
import StatsGrid from "../admin/statsCard/StatsCard";
import type { alertItem } from "./components/alertList/types.props";
import type { simpleAlertItem, performanceAlertItem } from "./types.props";

const ALERT_CONFIG = [
    {
        key: "unassigned_students",
        title: "Estudiantes sin asignaciones",
        Icon: UserX,
        getLink: (item: alertItem) =>
            `/facilitator-panel/user-management/${item.id}/assignments`,
    },
    {
        key: "unconfigured_simulations",
        title: "Simulacros sin configurar",
        Icon: FileWarning,
        getLink: (item: alertItem) =>
            `/facilitator-panel/simulations/${item.id}/configure`,
    },
    {
        key: "best_performing_students",
        title: "Estudiantes destacados",
        Icon: TrendingDown,
        getLink: (item: alertItem, simulationId?: string | number) =>
            `/student-dashboard?simulationId=${simulationId}&studentId=${item.id}`,
    },
];

const FacilitatorPanelPage: React.FC = () => {
    const navigate = useNavigate();
    const { panelData, isLoading, error, simulationIdForPerformance } =
        useFacilitatorPanel();

    useEffect(() => {
        document.body.classList.add("solid-public-bg");
        return () => document.body.classList.remove("solid-public-bg");
    }, []);

    if (isLoading) return <Spinner text="Cargando panel de control..." />;
    if (error)
        return (
            <AlertMessage type="error" title="Error al cargar panel">
                {error.generalMessage}
            </AlertMessage>
        );
    if (!panelData)
        return (
            <AlertMessage type="info" title="Sin datos">
                No hay datos disponibles para mostrar.
            </AlertMessage>
        );

    const { summary_users, summary_simulations, quick_alerts, activity_history } =
        panelData;

    const alertMappers: {
        unassigned_students: (item: simpleAlertItem) => alertItem;
        unconfigured_simulations: (item: simpleAlertItem) => alertItem;
        best_performing_students: (item: performanceAlertItem) => alertItem;
    } = {
        unassigned_students: (student) => ({
            id: student.id,
            primaryText: student.name,
        }),
        unconfigured_simulations: (sim) => ({
            id: sim.id,
            primaryText: sim.name,
        }),
        best_performing_students: (student) => ({
            id: student.id,
            primaryText: student.name,
            secondaryText: `Puntaje: ${student.avg_score.toFixed(2)}`,
        }),
    };

    const userStats = [
        {
            icon: <Users />,
            label: "Total de Estudiantes",
            value: summary_users.total_students,
            iconColor: "#3b82f6",
        },
        {
            icon: <CheckCircle />,
            label: "Estudiantes Activos",
            value: summary_users.active_students,
            iconColor: "#10b981",
        },
        {
            icon: <XCircle />,
            label: "Estudiantes Inactivos",
            value: summary_users.inactive_students,
            iconColor: "#f59e0b",
        },
        {
            icon: <Building />,
            label: "Total de Instituciones",
            value: summary_users.total_institutions,
            iconColor: "#8b5cf6",
        },
    ];

    const simulationStats = [
        {
            icon: <ClipboardList />,
            label: "Simulacros Activos",
            value: summary_simulations.active_count,
            iconColor: "#10b981",
        },
        {
            icon: <CheckCircle />,
            label: "Simulacros Finalizados",
            value: summary_simulations.finished_count,
            iconColor: "#6b7280",
        },
        {
            icon: <Clock />,
            label: "Próximos Simulacros",
            value: summary_simulations.upcoming_count,
            iconColor: "#3b82f6",
        },
    ];

    return (
        <div className={styles["page-container"]}>
            <header className={styles["header"]}>
                <ManagementHeader showBackButton={false} showProfile={true} />
                <div className={styles["title-wrapper"]}>
                    <h1 className={styles["page-title"]}>Panel de control</h1>
                </div>
                <div className={styles["quick-actions"]}>
                    <Button
                        onClick={() => navigate("/facilitator-panel/user-management")}
                        variant="secondary"
                        leftIcon={<Users size={16} />}
                    >
                        Gestionar usuarios
                    </Button>
                    <Button
                        onClick={() => navigate("/facilitator-panel/simulations")}
                        variant="secondary"
                        leftIcon={<Settings size={16} />}
                    >
                        Gestionar simulacros
                    </Button>
                </div>
            </header>

            <main className={styles["main-grid"]}>
                <div className={styles["left-column"]}>
                    <section>
                        <Card title="Resumen de usuarios" icon={<Users />}>
                            <StatsGrid items={userStats} />
                        </Card>
                    </section>
                    <section>
                        <Card title="Resumen de simulacros" icon={<ClipboardList />}>
                            <StatsGrid items={simulationStats} />
                        </Card>
                    </section>
                    <section>
                        <HistoricalDataCard simulations={activity_history} />
                    </section>
                </div>

                <div className={styles["right-column"]}>
                    <Card
                        title="Alertas Rápidas"
                        icon={<AlertTriangle />}
                        className={styles["alerts-card"]}
                    >
                        {ALERT_CONFIG.map((config) => {
                            const items =
                                quick_alerts[config.key as keyof typeof quick_alerts] || [];
                            const mapper =
                                alertMappers[config.key as keyof typeof alertMappers];
                            const mappedItems = items.map((item) => mapper(item as any));

                            return (
                                <div key={config.key} className={styles["alert-category"]}>
                                    <div className={styles["alert-header"]}>
                                        <config.Icon size={20} />
                                        <h3>
                                            {config.title} ({mappedItems.length})
                                        </h3>
                                    </div>
                                    <AlertList
                                        items={mappedItems}
                                        onItemClick={(item) => {
                                            const link =
                                                config.key === "best_performing_students"
                                                    ? config.getLink(item, simulationIdForPerformance)
                                                    : config.getLink(item);
                                            if (link) navigate(link);
                                        }}
                                    />
                                </div>
                            );
                        })}
                    </Card>
                </div>
            </main>
        </div>
    );
};

export default FacilitatorPanelPage;
